<template>
  <div class="recap">
    <b-card no-body class="recap__wrapper">
      <b-card-header v-if="show_header" class="recap__header">
        <div class="recap__header-element recap__header-element--first">
          <div class="recap__header-item">
            <vb-icon
              class="recap__header-icon mr-1"
              :name="'calendar'"
              :color="sass('primary')"
              :size="16"
            />
            <span>{{ header.checkin }} - {{ header.checkout }}</span>
          </div>
          <div class="recap__header-item">
            <vb-icon
              class="recap__header-icon mr-1"
              :name="'occupants'"
              :color="sass('primary')"
              :size="16"
            />
            <span class="semibold">{{ header.adults }}</span>
            <span
              v-if="header.children"
              class="ml-1"
            >e {{ header.children }}</span>
          </div>
        </div>
        <div class="recap__header-element recap__header-element--second">
          <div class="recap__header-item recap__header-item--hotel">
            <vb-icon
              class="recap__header-icon mr-1"
              :name="'pin'"
              :color="sass('primary')"
              :size="12"
            />
            <span class="semibold">{{ header.hotel_name }}</span>
            <span>{{ header.hotel_address }}</span>
          </div>
        </div>
      </b-card-header>
      <b-card-body>
        <div
          v-for="(room, room_index) in parse_rooms"
          :key="_uid + 'recap_room' + room_index"
          class="recap__room"
        >
          <div class="recap__room-head">
            <span class="recap__room-title">{{ room.name }}</span>
          </div>
          <div class="recap__products">
            <div
              v-for="(product, product_index) in room.products"
              :key="_uid + 'recap_product' + product_index"
              class="recap__product"
            >
              <div class="recap__product-info">
                <occupancy-product
                  :id="product.id"
                  class="mr-3"
                  :maximum_occupancy="product.maximum_occupancy"
                  @badge-tooltip="set_badge_tooltip"
                />
                <mealplan
                  v-if="product.policies.meal_plan.meals.length"
                  :meals="product.policies.meal_plan.meals"
                />
                <refundability :cancellation="product.policies.cancellation" />
              </div>
              <div class="recap__product-notify">
                <b-badge variant="light">
                  {{ badge_tooltip[product.id] }}
                </b-badge>
                <b-alert
                  v-if="show_matching && alert[room_index] && alert[room_index][product_index]"
                  show
                  :set_alert="badge = alert[room_index][product_index]"
                  :variant="badge.type.variant"
                >
                  <vb-icon
                    class="mr-1"
                    :name="badge.type.icon"
                    :color="badge.type.icon_color"
                    :size="10"
                  />
                  <div class="d-flex align-items-center">
                    <strong class="semibold">Gruppo {{ badge.allocation.index + 1 }}:</strong>
                    <occupancy-icons
                      :occupancy="badge.allocation"
                      :color="badge.type.icon_color"
                    />
                  </div>
                  <span>{{ badge.type.text }}</span>
                </b-alert>
              </div>
            </div>
          </div>
        </div>
        <b-card-text
          v-if="show_price || overcapacity_notice"
          class="recap__footer"
        >
          <span v-if="overcapacity_notice" class="recap__overcapacity">
            <vb-icon :name="'exclamation'" :color="sass('danger')" :size="14" />
            <span class="recap__overcapacity-text">{{
              overcapacity_notice
            }}</span>
          </span>
          <span v-if="show_price" class="recap__price">
            <span>include gestione e assistenza</span>
            <span>€ {{ solution.pricing.live_price_no_tax }}</span>
          </span>
        </b-card-text>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
  import { parse_date } from '@utilities/commons';
  import Mealplan from '@bookingv3/components/atoms/mealplan';
  import Refundability from '@bookingv3/components/atoms/refundability';
  import OccupancyProduct from '@bookingv3/components/blocks/occupancy-product';
  import OccupancyIcons from '@bookingv3/components/atoms/occupancy_icons';

  export default {
    name: 'card-recap',

    components: {
      mealplan: Mealplan,
      refundability: Refundability,
      'occupancy-product': OccupancyProduct,
      'occupancy-icons': OccupancyIcons,
    },

    props: {
      solution: {
        type: Object,
        default: () => ({}),
      },
      show_header: {
        type: Boolean,
        default: true,
      },
      show_price: {
        type: Boolean,
        default: true,
      },
      show_matching: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        badge_tooltip: {},
      };
    },

    computed: {
      // Genera l'header della card recap
      header() {
        const { occupancy, hotel_name, hotel_address, accommodation } =
          this.solution;
        const { checkin, checkout } = accommodation;

        const { adults, children } = occupancy.reduce(
          (total, item) => {
            total.adults += item.adults;
            total.children += item.children.length;
            return total;
          },
          { adults: 0, children: 0 }
        );

        const adults_label = adults > 1 ? 'adulti' : 'adulto';
        const children_label = children > 1 ? 'bambini' : 'bambino';

        const get_date = (date) => {
          const parsed = parse_date(date);
          return `${parsed.day_number} ${parsed.month_string} ${parsed.year}`;
        };

        return {
          checkin: get_date(checkin),
          checkout: get_date(checkout),
          adults: `${adults} ${adults_label}`,
          children: children > 0 ? `${children} ${children_label}` : null,
          hotel_name,
          hotel_address,
        };
      },

      // Genera le camere in base alla quantità scelta, es. 2 quantità di un prodotto genera 2 camere distinte
      parse_rooms() {
        const { rooms } = this.solution;
        const parsed_rooms = [];

        rooms.forEach((room) => {
          const new_prod = [];
          Object.values(room.products).forEach((prod) => {
            const { qty } = prod.price_selected;
            for (let i = 0; i < qty; i++) {
              new_prod.push(prod);
            }
          });
          parsed_rooms.push({
            id_room: room.id_room,
            name: room.name,
            products: new_prod,
          });
        });

        return parsed_rooms;
      },

      // Genera la struttura dati per i badge delle allocazioni. Lo stato del
      // badge (success/danger) deriva da occupancy_mismatch (fonte API), non più
      // da match_rooms.
      alert() {
        // pool consumabile: ogni istanza di prodotto in parse_rooms prende
        // un'entry distinta di accommodation.products (correlazione per id con
        // consumo), così prodotti con lo stesso id (qty>1) ottengono allocazioni
        // distinte invece della stessa ripetuta
        const pool = [...this.solution.accommodation.products];

        const take = (id) => {
          const index = pool.findIndex((product) => product.id === id);
          return index !== -1 ? pool.splice(index, 1)[0] : null;
        };

        return this.parse_rooms.map((room) =>
          room.products.map((product) => {
            const accommodation_product = take(product.id);

            // senza corrispondenza il badge non viene renderizzato (guardia nel
            // template) → niente crash
            if (!accommodation_product || !accommodation_product.allocation) {
              return null;
            }

            const allocation = { ...accommodation_product.allocation };

            // crea la chiave adults per il componente delle iconcine
            allocation.adults = allocation.number_of_adults;
            // cancella la chiave number_of_adults sostituita da adults
            delete allocation.number_of_adults;

            return {
              ...accommodation_product,
              allocation,
              type: this.match_type(accommodation_product.occupancy_mismatch),
            };
          })
        );
      },

      /**
       * Avviso mostrato quando la soluzione ha una capienza massima superiore
       * al numero di ospiti per cui si sta effettivamente prenotando (la ricerca).
       *
       * Serve a chiarire che, pur avendo scelto ad es. una quadrupla, la
       * prenotazione vale per gli ospiti della ricerca (es. 2) e non per la
       * capienza massima della camera.
       *
       * Confronta il totale ospiti della ricerca (occupancy) con il totale
       * ospiti della capienza massima (somma di maximum_occupancy.total su
       * tutti i prodotti della soluzione).
       *
       * Ritorna null se non c'è capienza in eccesso, altrimenti la stringa
       * dell'avviso.
       */
      overcapacity_notice() {
        // occupancy della ricerca: per quanti ospiti si sta prenotando
        const booked = this.solution.occupancy.reduce(
          (total, item) => {
            total.adults += item.adults;
            total.children += item.children.length;
            return total;
          },
          { adults: 0, children: 0 }
        );

        // capienza massima totale della soluzione (somma dei total dei prodotti)
        const capacity_total = this.parse_rooms.reduce(
          (total, room) =>
            total +
            room.products.reduce(
              (acc, product) => acc + product.maximum_occupancy.total,
              0
            ),
          0
        );

        // mostra l'avviso solo se la capienza supera gli ospiti prenotati
        if (capacity_total <= booked.adults + booked.children) return null;

        // sostantivi con accordo singolare/plurale in base al numero di ospiti
        const adulti = `${booked.adults} ${booked.adults === 1 ? 'adulto' : 'adulti'}`;

        // la parte bambini è omessa quando la ricerca non ne prevede
        const bambini =
          booked.children > 0
            ? ` e ${booked.children} ${booked.children === 1 ? 'bambino' : 'bambini'}`
            : '';

        return `La soluzione prevede una capienza massima superiore, ma stai prenotando per ${adulti}${bambini}. Eventuali ospiti aggiuntivi richiedono la conferma della struttura e possono comportare supplementi. Per prenotare per più ospiti, effettua una nuova ricerca.`;
      },
    },

    watch: {},

    methods: {
      set_badge_tooltip(id, tooltip) {
        this.$set(this.badge_tooltip, id, tooltip);
      },

      // Config del badge in base a occupancy_mismatch (fonte API): assente →
      // soluzione adatta (success), presente → camera non adatta (danger).
      // Il dettaglio degli ospiti non allocati è mostrato nell'avviso block-level.
      match_type(occupancy_mismatch) {
        if (!occupancy_mismatch) {
          return {
            text: 'OK!',
            icon: 'check_circle',
            icon_color: '0c5028',
            variant: 'success',
          };
        }

        return {
          text: 'Camera non adatta',
          icon: 'exclamation',
          icon_color: '7b151c',
          variant: 'danger',
        };
      },
    },
  };
</script>

<style lang="scss" scoped>
  .recap {
    &__title {
      margin-bottom: 20px;

      @include media-breakpoint-up(md) {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
      }

      .btn {
        margin-top: 20px;

        @include media-breakpoint-up(md) {
          margin-top: 0;
        }

        &-white {
          background-color: $white;
        }
      }
    }

    &__wrapper {
      border-radius: 10px;
      border: none;
    }

    &__header {
      $this_recap_header: &;
      background-color: #fafcff;
      border-bottom: 1px solid $gray-300;
      display: flex;
      align-items: center;
      justify-content: space-between;

      &:first-child {
        border-radius: 10px 10px 0 0;
      }

      &-element {
        &--first {
          display: flex;
          align-items: center;
        }
      }

      &-item {
        display: flex;
        align-items: center;
        margin-left: 15px;

        &:first-child {
          margin-left: 0;
        }

        @at-root #{$this_recap_header}-element--first & {
          font-weight: $vb-font-weight-semibold;
        }

        &--hotel {
          font-size: $font-size-md;

          span {
            &:last-child {
              margin-left: 10px;
            }
          }
        }
      }
    }

    &__room {
      border-top: 1px solid $gray-300;
      margin-top: 10px;
      padding-top: 10px;

      &:first-child {
        padding-top: 0;
        margin-top: 0;
        border-top: 0;
      }

      &-head {
        display: flex;
        align-items: center;
        font-weight: $vb-font-weight-semibold;
      }
    }

    &__products {
      margin-left: 10px;
      margin-top: 10px;
    }

    &__product {
      border-top: 1px solid $gray-200;
      margin-top: 10px;
      padding-top: 10px;
      text-align: center;

      @include media-breakpoint-up(md) {
        text-align: left;
      }

      &:first-child {
        margin-top: 0;
        padding-top: 0;
        border-top: 0;
      }

      &-info {
        @include media-breakpoint-up(sm) {
          display: flex;
          align-items: center;
        }
      }

      &-notify {
        text-align: left;
        margin-top: 10px;
        display: flex;
        align-items: center;
        flex-wrap: wrap;

        .badge {
          font-weight: normal;
          font-size: $font-size-md;
          padding: 5px;
          margin-right: 10px;
          border: 1px solid transparent;
          margin-bottom: 5px;

          @include media-breakpoint-up(sm) {
            margin-bottom: 0;
          }
        }

        .alert {
          display: flex;
          align-items: center;
          margin-bottom: 5px;
          padding: 5px;
          font-size: $font-size-md;
          line-height: 1;
          border-radius: $border-radius-lg;

          @include media-breakpoint-up(sm) {
            margin-bottom: 0;
          }

          &-danger {
            span {
              color: #7b151c;
            }
          }

          .occupancy-icons {
            background-color: transparent;
            padding: 0;
            margin: -2px 0px 0 5px;
            position: relative;
            top: 1px;
            align-items: center;

            &:after {
              content: '|';
              margin: -2px 6px 0;
            }
          }
        }
      }
    }

    &__footer {
      display: flex;
      justify-content: space-between;
      border-top: 1px solid $gray-200;
      padding-top: 10px;
      margin-top: 10px;

      & > * {
        flex-basis: 100%;

        & + * {
          flex-basis: 30%;
        }
      }
    }

    &__price {
      span {
        &:first-child {
          color: $gray-500;
          font-size: $font-size-md;
          margin-right: 10px;
        }
        &:last-child {
          color: $primary;
          font-size: $font-size-lg;
          font-weight: $vb-font-weight-semibold;
        }
      }
    }

    &__overcapacity {
      font-size: $font-size-md;
      text-align: left;
      color: $danger;
      line-height: normal;
      display: flex;
      align-self: center;

      .vb-icon {
        margin-right: $grid-gutter-width / 4;
        margin-top: $grid-gutter-width / 10;
      }
    }

    ::v-deep .occupancy-product {
      display: flex;
      align-items: center;

      &__alert {
        margin-left: $grid-gutter-width / 3;
      }
    }
  }
</style>
